import { useQueryClient } from "@tanstack/react-query";
import { createBrowserRouter, RouterProvider } from "react-router";
import { AuthLayout, ErrorState, LoginScreen, RestrictedGate, Spinner } from "@hostdock/ui";
import { api, client, SESSION_KEY } from "./api";
import { PanelLayout } from "./layout";
import { AccountPage } from "./pages/account";
import { AppsPage } from "./pages/apps";
import { AppInstallPage } from "./pages/app-install";
import { AppInstallationPage } from "./pages/app-installation";
import { HomePage } from "./pages/home";
import { NotFoundPage } from "./pages/not-found";
import { DnsPage, DnsZonePage } from "./pages/dns";
import { DevelopersPage } from "./pages/developers";
import { EmailDomainPage, EmailPage } from "./pages/email";
import { MigrationDetailPage, MigrationsPage, NewMigrationPage } from "./pages/migrate";
import { SecurityPage } from "./pages/security";
import { CreateWebsitePage } from "./pages/create-website";
import { DatabasesPage } from "./pages/databases";
import { DomainsPage } from "./pages/domains";
import { WebsiteDetailPage } from "./pages/websites";
import { WebsitesPage } from "./pages/websites-list";
import { SessionProvider, useSessionQuery, useSetSession } from "./session";

const router = createBrowserRouter([
  {
    element: <PanelLayout />,
    children: [
      { index: true, element: <HomePage /> },
      { path: "websites", element: <WebsitesPage /> },
      { path: "websites/new", element: <CreateWebsitePage /> },
      { path: "websites/:id", element: <WebsiteDetailPage /> },
      { path: "apps", element: <AppsPage /> },
      { path: "apps/installations/:id", element: <AppInstallationPage /> },
      { path: "apps/:id", element: <AppInstallPage /> },
      { path: "domains", element: <DomainsPage /> },
      { path: "databases", element: <DatabasesPage /> },
      { path: "dns", element: <DnsPage /> },
      { path: "dns/:id", element: <DnsZonePage /> },
      { path: "developers", element: <DevelopersPage /> },
      { path: "email", element: <EmailPage /> },
      { path: "email/:id", element: <EmailDomainPage /> },
      { path: "migrate", element: <MigrationsPage /> },
      { path: "migrate/new", element: <NewMigrationPage /> },
      { path: "migrate/:id", element: <MigrationDetailPage /> },
      { path: "security", element: <SecurityPage /> },
      { path: "account", element: <AccountPage /> },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);

export function App() {
  const session = useSessionQuery();
  const setSession = useSetSession();
  const qc = useQueryClient();

  if (session.isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner label="Loading HostDock" />
      </div>
    );
  }
  if (session.error) {
    return (
      <AuthLayout product="Panel">
        <ErrorState error={session.error} onRetry={() => void session.refetch()} />
      </AuthLayout>
    );
  }
  if (!session.data) {
    return (
      <AuthLayout product="Panel">
        <LoginScreen api={api.account} title="Sign in to HostDock" onAuthenticated={setSession} />
      </AuthLayout>
    );
  }
  if (session.data.restriction) {
    return (
      <RestrictedGate
        product="Panel"
        restriction={session.data.restriction}
        api={api.account}
        onResolved={() => void qc.invalidateQueries({ queryKey: SESSION_KEY })}
        onLogout={async () => {
          await api.account.logout().catch(() => undefined);
          client.setCsrfToken(null);
          setSession(null);
        }}
      />
    );
  }
  return (
    <SessionProvider session={session.data}>
      <RouterProvider router={router} />
    </SessionProvider>
  );
}
